import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Flex,
  Heading,
  IconOnePass,
  Link,
  LoadingSpinner,
  Modal,
  RadioGroup,
  Span,
  Text,
} from "@catchoftheday/cg-components";
import { formatPrice } from "@catchoftheday/utilities";
import { useAddToCartToast } from "@catchoftheday/cart-app";
import {
  emitAddToCart,
  emitOpenCCExModal,
  getSourcePlatform,
  trackLinkClick,
} from "@catchoftheday/analytics";
import { useEventBus, legacyEventBus } from "@catchoftheday/state";
import { css } from "@emotion/react";
import { mutate } from "swr";
import { CONSTANTS as HEADER_APP_CONSTANTS } from "@catchoftheday/header-app";
import { addToCart as addToCartLegacy } from "@catchoftheday/search-app";
import {
  TRIGGER_ONEPASS_PLAN_MODAL_EVENT,
  TRIGGER_ONEPASS_EXCLUSIVE_PRODUCT_MODAL_EVENT,
} from "./triggers";
import { PlanOptionRadio } from "./PlanOptionRadio";
import { benefits } from "./benefits";
import {
  OnePassOptionFragment,
  useOnePassMembershipModalLazyQuery,
} from "@common-vendors/generated/graphql";
import {
  DEFAULT_CATCH_SELLER_ID,
  DEFAULT_CLUB_CATCH_EVENT_ID,
  LOGIN_URL,
  ONEPASS_LEARN_MORE,
  ONEPASS_TERMS_AND_CONDITIONS,
} from "@common-vendors/consts";

type ModalToOpen = "plan-selection" | "exclusive-product" | null;

export type MemberExclusiveProductProps = {
  imageUrl: string;
  productId: number;
  productTitle: string;
  price: number;
  eventId: number;
  offerId: number;
  sellerId: number;
};

type OnePassMembershipModalProps = {
  /** setLoading to be passed into useToast callback */
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  /** setError to be passed into useToast callback */
  setError?: React.Dispatch<React.SetStateAction<string | undefined>>;
};

export const OnePassMembershipModal = ({
  setLoading,
  setError,
}: OnePassMembershipModalProps) => {
  const [getData, { data, loading, error }] =
    useOnePassMembershipModalLazyQuery({
      fetchPolicy: "network-only",
    });
  const cartSelectedOption = data?.cart.clubSubscription.selectedOption;
  const [modalToOpen, setModalToOpen] = useState<ModalToOpen>(null);
  const [product, setProduct] = useState<MemberExclusiveProductProps>();
  const onePassSubscriptionOptions = data?.cart.clubSubscription.options;

  const onePassMonthlyOption = onePassSubscriptionOptions?.find(
    ({ interval }) => interval == "MONTHLY"
  ) as OnePassOptionFragment;

  const onePassYearlyOption = onePassSubscriptionOptions?.find(
    ({ interval }) => interval == "YEARLY"
  ) as OnePassOptionFragment;

  const isLapsedOnePassMember =
    data?.customer?.club && !data.customer.club.active;

  const addToCart = useAddToCartToast({
    setLoading,
    setError,
    showCheckoutOnSuccess: false,
    showTickOnSuccess: true,
  });

  const onAddToCart = useCallback(
    async (selectedOption: OnePassOptionFragment) => {
      if (selectedOption) {
        setModalToOpen(null);
        if (modalToOpen === "exclusive-product" && !!product) {
          await addToCartLegacy({
            eventId: DEFAULT_CLUB_CATCH_EVENT_ID,
            title: selectedOption.title,
            sellerId: DEFAULT_CATCH_SELLER_ID,
            id: parseInt(selectedOption.id),
            offerId: parseInt(selectedOption.id),
            imageUrl: selectedOption.clubImage?.url,
          });

          await addToCartLegacy({
            eventId: product.eventId,
            title: product.productTitle,
            sellerId: product.sellerId,
            id: product.productId,
            offerId: product.offerId,
            imageUrl: product.imageUrl,
          });

          mutate(HEADER_APP_CONSTANTS.HEADER_MINICART_API);
        } else {
          // add membership item to cart
          const { status: addMembershipStatus } = await addToCart(
            {
              imageUrl: selectedOption.clubImage?.url,
              title: selectedOption.title,
            },
            {
              offerId: selectedOption.id,
              quantity: 1,
              sourcePlatform: getSourcePlatform(),
            }
          );
          if (addMembershipStatus !== "ServerError") {
            emitAddToCart({
              customer_id: data?.customer?.id,
              success: addMembershipStatus == "OK",
              offer_id: selectedOption.id,
              event_id: DEFAULT_CLUB_CATCH_EVENT_ID,
            });
          }
        }
      }
    },
    [addToCart, data?.customer?.id, modalToOpen, product]
  );

  const handleRadioChange = (value: string) => {
    if (!!modalToOpen && onePassSubscriptionOptions?.length) {
      const selectedOption = onePassSubscriptionOptions?.find(
        (opt) => opt.id === value
      ) as OnePassOptionFragment;
      onAddToCart(selectedOption);
    }
  };

  const handleModalOpen = (modal: ModalToOpen) => {
    if (!data) {
      getData();
    }
    if (!loading && !error) {
      setModalToOpen(modal);
    }
  };

  const handleModalClose = () => {
    setModalToOpen(null);
  };

  const handleModalCloseWithTracking = () => {
    trackLinkClick({
      targetUrl: window.location.href,
      elementClasses: ["close", "js-modal-close"],
    });
    handleModalClose();
  };

  useEventBus<MemberExclusiveProductProps>(
    TRIGGER_ONEPASS_EXCLUSIVE_PRODUCT_MODAL_EVENT,
    (event) => {
      if (event.payload) {
        setProduct(event.payload);
        const { productId, productTitle } = event.payload;
        emitOpenCCExModal({
          productId: `${productId}`,
          productName: productTitle,
        });
      }
      handleModalOpen("exclusive-product");
    }
  );

  useEventBus(TRIGGER_ONEPASS_PLAN_MODAL_EVENT, () => {
    handleModalOpen("plan-selection");
  });

  useEffect(() => {
    legacyEventBus?.on(TRIGGER_ONEPASS_PLAN_MODAL_EVENT.type, () => {
      getData();
      if (!loading && !error) {
        setModalToOpen("plan-selection");
      }
    });
  }, [error, loading, data, getData]);

  if (error) {
    return null;
  }

  return (
    <Modal
      isOpen={!!modalToOpen}
      contentProps={{
        p: "0px",
        textAlign: "center",
        position: "relative",
        color: "onePass.textPrimary",
        fontFamily: "onePass",
        fontWeight: "medium",
      }}
      onRequestClose={handleModalClose}
      onCloseBtnClick={handleModalCloseWithTracking}
      maxWidth="520px"
      marginX="10px"
      height={{
        xs: "auto",
      }}
      zIndex={101}
    >
      {loading && (
        <LoadingSpinner
          containerProps={{ "aria-label": "loading spinner", zIndex: 1 }}
          hasOverlay
          ringBg="ui.borderColor"
        />
      )}
      <Box
        backgroundColor="onePass.primary"
        textAlign="center"
        py="24px"
        mb="16px"
        color="textWhite"
      >
        <IconOnePass
          aria-label="OnePass"
          height="27px"
          width="auto"
          color="inherit"
        />
      </Box>
      {!!cartSelectedOption && (
        <Alert
          type="inline"
          variant="info"
          wrapperProps={{
            px: "14px",
            mb: "16px",
          }}
          fontSize={{
            xs: "xs",
            md: "sm",
          }}
          fontWeight="medium"
          textAlign="left"
        >
          You already have a OnePass membership in your cart
        </Alert>
      )}
      <Box px="24px" pb="24px">
        <Heading.h2 textAlign="center" m={0} mb="12px">
          {isLapsedOnePassMember
            ? "Incredible Benefits"
            : "Start free 30 day trial"}
        </Heading.h2>
        <Flex alignItems="center" flexWrap="wrap">
          {benefits.map((benefit, idx) => (
            <Flex
              key={`onepass-benefit-${idx}`}
              alignItems="center"
              py={{
                xs: "10px",
                lg: "8px",
              }}
              px="7px"
              width={{
                xs: "100%",
                lg: "50%",
              }}
            >
              <Flex minWidth="60px">{benefit.icon}</Flex>
              <Box>
                <Heading.h5 textAlign="left" m="0" fontWeight="bold">
                  {benefit.title}
                </Heading.h5>
                <Text fontSize="xs" m="0" fontWeight="inherit" textAlign="left">
                  {benefit.subtitle}
                </Text>
              </Box>
            </Flex>
          ))}
        </Flex>
        <Box as="hr" borderColor="onePass.primaryDarker" m="0" my="14px" />
        <Heading.h3 textAlign="center" mb="8px" fontWeight="bold">
          Choose your membership
        </Heading.h3>
        <Text
          color="textTertiary"
          fontWeight="medium"
          fontSize="xs"
          mt={0}
          mb="17px"
        >
          {!isLapsedOnePassMember
            ? `After your 30 day free trial, OnePass is only ${formatPrice(
                onePassMonthlyOption?.price?.amount,
                false
              )}/month, or you can save more at ${formatPrice(
                onePassYearlyOption?.price?.amount,
                false
              )}/year. Cancel anytime.`
            : `OnePass is an ongoing membership program that you may cancel anytime. Save even more with our annual plan!`}
        </Text>
        <RadioGroup
          justifyContent="center"
          alignItems="center"
          name="onePassOption"
          display="flex"
          flexDirection="row"
          onChange={(e: React.FormEvent<HTMLInputElement>) =>
            handleRadioChange(e.currentTarget.value)
          }
          css={css`
            label {
              padding: 0px 8px;

              &:first-of-type {
                padding-left: 0px;
              }

              &:last-child {
                padding-right: 0px;
              }
            }
          `}
        >
          {onePassSubscriptionOptions?.map((option) => (
            <PlanOptionRadio
              key={option.id}
              onePassSubscriptionOption={option}
              onChange={(value) => handleRadioChange(value)}
              isFreeTrialEligible={!isLapsedOnePassMember}
            />
          ))}
        </RadioGroup>
        {!data?.customer && (
          <Text mt={0} fontSize="sm" fontWeight="medium">
            <Span fontSize="inherit" fontWeight="inherit">
              Already a member?
            </Span>{" "}
            <Link
              fontSize="inherit"
              fontWeight="inherit"
              href={LOGIN_URL}
              data-tracking="onepass-membership-modal-login"
            >
              Login
            </Link>
          </Text>
        )}
        <Span
          whiteSpace="nowrap"
          color="onePass.primary"
          fontWeight="bold"
          fontSize="xs"
        >
          <Link color="inherit" href={ONEPASS_LEARN_MORE}>
            Learn More
          </Link>{" "}
          |{" "}
          <Link color="inherit" href={ONEPASS_TERMS_AND_CONDITIONS}>
            Terms &amp; Conditions
          </Link>
        </Span>
      </Box>
    </Modal>
  );
};
